import { Drawer, message, Select, InputNumber } from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createProvidentFund, fetchAllProvidentFunds, updateProvidentFund } from "../../../core/data/redux/payroll/Accounting/providentFund/providentFundActions";
import { fetchEmployeeSalaryStructures } from "../../../core/data/redux/payroll/payrollStructure/payrollStructure/employeeSalaryStructureActions";
import { status } from "../../../core/common/selectoption/selectoption";
import { AppDispatch } from "../../../core/data/redux/store";
import { fetchAllPfEmployees } from "../../../core/data/redux/payroll/Accounting/providentFund/pfEmployee/pfEmployeeActions";
import { useAppConfig } from "../../../customhooks/useAppConfig";
import { calculatePfAmounts } from "../utils/pfCalculationUtils";
import { fetchAllPfPolicies } from "../../../core/data/redux/payroll/Accounting/providentFund/pfpolicies/pfPolicyActions";
import dayjs from "dayjs";
import { filterByStatus } from "../../../core/utils/filterActiveData";

const to2 = (v: any) => Number(Number(v ?? 0).toFixed(2));

// display with two decimals (e.g., 1200.00, 145.23)
const fmt2 = (v?: string | number | null) => {
    const n = Number(v ?? 0);
    if (!Number.isFinite(n)) return "0.00";
    return n.toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};
// parser that returns a number (for TS correctness)
const parse2 = (display?: string) => {
    if (display == null) return 0;
    const cleaned = display.replace(/,/g, "").trim();
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
};

// find if structure overlaps with a Year-Month ("YYYY-MM")
const overlapsMonth = (startISO?: string, endISO?: string, ym?: string) => {
    if (!startISO || !ym) return false;
    const s = dayjs(startISO);
    const e = endISO ? dayjs(endISO) : dayjs("2999-12-31");
    const mStart = dayjs(`${ym}-01`);
    const mEnd = mStart.endOf("month");
    return !(e.isBefore(mStart, "day") || s.isAfter(mEnd, "day"));
};

const ProvidentFundModal: React.FC<any> = ({
    modalType,
    handleCancel,
    isModalVisible,
    selectedRowData,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const { user } = useSelector((state: any) => state.auth);
    const { loadingStates } = useSelector(
        (state: any) => state.providentFund.employeeContribution
    );
    const { salaryStructures = [] } = useSelector(
        (state: any) => state.payroll.salaryStructure
    );
    const { records: pfemployeeMapping = [] } = useSelector(
        (state: any) => state.providentFund.pfEmployeeMapping
    );

    const records = filterByStatus(pfemployeeMapping, 'active', 'status');

    const { records: PfPolicy = [] } = useSelector((state: any) => state.providentFund.pfPolicy);

    const config = useAppConfig();
    const [autoFillPolicyKey, setAutoFillPolicyKey] = useState<string>("");
    const [lastAutoFillEarningsKey, setLastAutoFillEarningsKey] =
        useState<string>("");

    const isLoading =
        modalType === "edit" ? loadingStates.update : loadingStates.create;

    const [formData, setFormData] = useState<{
        pfEmployeeId: any;
        provident_fund_type: string;
        employee_share_percent: number;
        organization_share_percent: number;
        employee_share_amount: number;
        organization_share_amount: number;
        description: string;
        status: string;
        createdBy: number;
        selected_earning_ids: number[];
        year: number;
        month: number;
    }>({
        pfEmployeeId: "",
        provident_fund_type: "Select",
        employee_share_percent: 0,
        organization_share_percent: 0,
        employee_share_amount: 0,
        organization_share_amount: 0,
        description: "",
        status: "active",
        createdBy: user?.UserId || 1,
        selected_earning_ids: [],
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
    });

    const [errors, setErrors] = useState<any>({});

    // fetch base data
    useEffect(() => {
        dispatch(fetchEmployeeSalaryStructures());
        dispatch(fetchAllPfPolicies());
        if (!records.length) dispatch(fetchAllPfEmployees());
    }, [dispatch]); // eslint-disable-line

    // edit prefill
    useEffect(() => {
        if (modalType === "edit" && selectedRowData) {
            setFormData({
                pfEmployeeId: Number(selectedRowData.pfEmployeeId ?? ""),
                provident_fund_type: selectedRowData.provident_fund_type,
                employee_share_percent: selectedRowData.employee_share_percent,
                organization_share_percent: selectedRowData.organization_share_percent,
                employee_share_amount: selectedRowData.employee_share_amount,
                organization_share_amount: selectedRowData.organization_share_amount,
                selected_earning_ids: (selectedRowData.selected_earning_ids || []).map(
                    (id: any) => Number(id)
                ),
                description: selectedRowData.description,
                status: selectedRowData.status,
                createdBy: selectedRowData.createdBy,
                year: selectedRowData.year,
                month: selectedRowData.month,
            });
            setErrors({});
        } else {
            resetForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalType, selectedRowData]);

    const resetForm = () => {
        setFormData({
            pfEmployeeId: "",
            provident_fund_type: "Select",
            employee_share_percent: 0,
            organization_share_percent: 0,
            employee_share_amount: 0,
            organization_share_amount: 0,
            selected_earning_ids: [],
            description: "",
            status: "active",
            createdBy: user?.UserId || 1,
            year: new Date().getFullYear(),
            month: new Date().getMonth() + 1,
        });
        setErrors({});
        setAutoFillPolicyKey("");
        setLastAutoFillEarningsKey("");
    };

    const handleChange = (field: string, value: any) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setErrors((prev: any) => ({ ...prev, [field]: "" }));
    };

    const validateForm = () => {
        let valid = true;
        const newErrors: any = {
            pfEmployeeId: "",
            provident_fund_type: "",
            employee_share_percent: "",
            organization_share_percent: "",
            selected_earning_ids: "",
            description: "",
            status: "",
            year: "",
            month: "",
        };

        if (!formData.pfEmployeeId) {
            newErrors.pfEmployeeId = "Employee is required.";
            valid = false;
        }
        if (formData.provident_fund_type === "Select") {
            newErrors.provident_fund_type = "Provident fund type is required.";
            valid = false;
        }
        if (formData.selected_earning_ids.length === 0) {
            newErrors.selected_earning_ids = "At least one earning must be selected.";
            valid = false;
        }
        if (!config?.pf_reporting_enabled && formData.employee_share_percent < 6) {
            newErrors.employee_share_percent =
                "Minimum 6% employee contribution is required.";
            valid = false;
        } else if (formData.employee_share_percent <= 0) {
            newErrors.employee_share_percent =
                "Employee share (%) must be a positive number.";
            valid = false;
        }
        if (!formData.status) {
            newErrors.status = "Status is required.";
            valid = false;
        }
        if (!formData.year) {
            newErrors.year = "Year is required.";
            valid = false;
        }
        if (!formData.month || formData.month < 1 || formData.month > 12) {
            newErrors.month = "Month must be between 1 and 12.";
            valid = false;
        }

        setErrors(newErrors);
        return valid;
    };

    // YEAR-MONTH key for structure selection
    const ymKey = useMemo(
        () => `${formData.year}-${String(formData.month).padStart(2, "0")}`,
        [formData.year, formData.month]
    );

    // resolve the *active* salary structure for employee & period
    const activeStructure: any = useMemo(() => {
        if (!formData.pfEmployeeId) return null;
        const mapping = records.find(
            (r: any) => Number(r.pfEmployeeId) === Number(formData.pfEmployeeId)
        );
        const empId = mapping?.EmployeeID;
        if (!empId) return null;

        const matches = (salaryStructures || [])
            .filter(
                (ss: any) =>
                    ss.employee_id === empId && overlapsMonth(ss.start_date, ss.end_date, ymKey)
            )
            .sort(
                (a: any, b: any) =>
                    dayjs(b.start_date).valueOf() - dayjs(a.start_date).valueOf()
            );

        return matches[0] || null;
    }, [formData.pfEmployeeId, records, salaryStructures, ymKey]);

    // sanitize selected earning ids when structure changes; auto-fill once
    useEffect(() => {
        if (!activeStructure) {
            if (modalType !== "edit") {
                setFormData((prev) => ({ ...prev, selected_earning_ids: [] }));
            }
            return;
        }

        const validIds = new Set(
            (activeStructure.earnings || []).map((e: any) => Number(e.id))
        );

        // 1) keep only ids that exist in the new structure
        setFormData((prev) => ({
            ...prev,
            selected_earning_ids: (prev.selected_earning_ids || []).filter((id: number) =>
                validIds.has(Number(id))
            ),
        }));

        // 2) auto-fill ONCE per employee+structure+period
        const key = `${formData.pfEmployeeId}:${activeStructure.structure_id || "s"}:${ymKey}`;
        if (modalType !== "edit" && key !== lastAutoFillEarningsKey) {
            const ids = (activeStructure.earnings || []).map((e: any) => Number(e.id));
            setFormData((prev) => ({ ...prev, selected_earning_ids: ids }));
            setLastAutoFillEarningsKey(key);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStructure, modalType, ymKey]);

    // compute contributions (always rounded to 2dp)
    useEffect(() => {
        if (activeStructure && formData.selected_earning_ids.length > 0) {
            const selectedRows = (activeStructure.earnings || []).filter((e: any) =>
                formData.selected_earning_ids.includes(e.id)
            );

            const { employeeShareAmount, organizationShareAmount } = calculatePfAmounts({
                providentFundType: formData.provident_fund_type as "mandatory" | "voluntary",
                employeeSharePercent: formData.employee_share_percent,
                organizationSharePercent: formData.organization_share_percent,
                selectedEarnings: selectedRows.map((e: any) => ({ id: e.id, amount: e.amount })),
            });

            setFormData((prev) => ({
                ...prev,
                employee_share_amount: to2(employeeShareAmount),
                organization_share_amount: to2(organizationShareAmount),
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                employee_share_amount: to2(0),
                organization_share_amount: to2(0),
            }));
        }
        // depend on everything that can affect the math
    }, [
        activeStructure,
        formData.selected_earning_ids,
        formData.employee_share_percent,
        formData.organization_share_percent,
        formData.provident_fund_type,
    ]);

    // auto-fill PF policy driven fields when Pf Employee is selected (CREATE only)
    useEffect(() => {
        if (!formData.pfEmployeeId || modalType === "edit") return;

        const mapping = records.find(
            (r: any) => Number(r.pfEmployeeId) === Number(formData.pfEmployeeId)
        );
        if (!mapping) return;

        const policy = (PfPolicy || []).find(
            (p: any) => Number(p.pfPolicyId) === Number(mapping.pfPolicyId)
        );
        if (!policy) return;

        const key = `${mapping.pfEmployeeId}:${mapping.pfPolicyId}`;
        if (key === autoFillPolicyKey) return; // prevent duplicate fills
        setAutoFillPolicyKey(key);

        const type =
            policy.isVoluntary === 1 ||
                /voluntary/i.test(String(policy.policyName || ""))
                ? "voluntary"
                : "mandatory";

        setFormData((prev) => ({
            ...prev,
            provident_fund_type: type,
            employee_share_percent: Number(policy.employeeContributionPercent) || 0,
            organization_share_percent: Number(policy.employerContributionPercent) || 0,
        }));
    }, [formData.pfEmployeeId, records, PfPolicy, modalType, autoFillPolicyKey]);

    const handleSubmit = async () => {
        if (!validateForm()) return;
        try {
            const payload = {
                ...formData,
                createdBy: user?.id || 1,
                modifiedBy: user?.id || 1,
                year: formData.year,
                month: formData.month,
            };
            if (modalType === "edit") {
                await dispatch(
                    updateProvidentFund({ id: selectedRowData.provident_fund_id, data: payload })
                ).unwrap();
                message.success("Provident fund updated successfully");
            } else {
                await dispatch(createProvidentFund(payload)).unwrap();
                message.success("Provident fund created successfully");
            }
            await dispatch(fetchAllProvidentFunds());
            handleCancel();
            resetForm();
        } catch (err: any) {
            const errorMessage = err?.error || "Something went wrong";
            message.error(errorMessage);
        }
    };

    return (
        <Drawer
            title={modalType === "edit" ? "Edit Provident Fund" : "Add Provident Fund"}
            placement="right"
            width={600}
            onClose={() => {
                handleCancel();
                resetForm();
            }}
            visible={isModalVisible}
            style={{ marginTop: 50, paddingBottom: 50 }}
        >
            <form>
                <div className="modal-body pb-0">
                    <div className="row">
                        {/* Year */}
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Year</label>
                            <Select
                                value={formData.year}
                                onChange={(value) => handleChange("year", value)}
                                className="w-100"
                                disabled={modalType === "edit"}
                            >
                                {Array.from({ length: 21 }, (_, i) => {
                                    const year = new Date().getFullYear() - 10 + i;
                                    return (
                                        <Select.Option key={year} value={year}>
                                            {year}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                            {errors.year && <div className="text-danger mt-1">{errors.year}</div>}
                        </div>

                        {/* Month */}
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Month</label>
                            <Select
                                value={formData.month}
                                onChange={(value) => handleChange("month", value)}
                                className={`w-100 ${errors.month ? "is-invalid-select" : ""}`}
                                disabled={modalType === "edit"}
                            >
                                {Array.from({ length: 12 }, (_, i) => (
                                    <Select.Option key={i + 1} value={i + 1}>
                                        {new Date(0, i).toLocaleString("default", { month: "long" })}
                                    </Select.Option>
                                ))}
                            </Select>
                            {errors.month && <div className="text-danger mt-1">{errors.month}</div>}
                        </div>

                        {/* PF Employee */}
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Employee Name</label>
                            <Select
                                value={formData.pfEmployeeId}
                                onChange={(value) => handleChange("pfEmployeeId", value)}
                                className={`w-100 ${errors.pfEmployeeId ? "is-invalid-select" : ""}`}
                            >
                                {records?.map((option: any) => (
                                    <Select.Option key={option.pfEmployeeId} value={option.pfEmployeeId}>
                                        {option.employeeName}
                                    </Select.Option>
                                ))}
                            </Select>
                            {errors.pfEmployeeId && (
                                <div className="text-danger mt-1">{errors.pfEmployeeId}</div>
                            )}
                        </div>

                        {/* PF Type */}
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Provident Fund Type</label>
                            <Select
                                value={formData.provident_fund_type}
                                onChange={(value) => handleChange("provident_fund_type", value)}
                                className={`w-100 ${errors.provident_fund_type ? "is-invalid-select" : ""}`}
                            >
                                <Select.Option value="mandatory">Mandatory Provident Fund</Select.Option>
                                <Select.Option value="voluntary">Voluntary Provident Fund</Select.Option>
                            </Select>
                            {errors.provident_fund_type && (
                                <div className="text-danger mt-1">{errors.provident_fund_type}</div>
                            )}
                        </div>

                        {/* Earnings + structure info */}
                        {activeStructure ? (
                            <div className="col-md-12 mb-3">
                                <label className="form-label">Select Earnings</label>
                                <small className="text-muted d-block mb-2">
                                    Using salary structure:{" "}
                                    <strong>{activeStructure.structure_name || "—"}</strong>{" "}
                                    (effective {dayjs(activeStructure.start_date).format("DD-MMM-YYYY")}{" "}
                                    to{" "}
                                    {activeStructure.end_date
                                        ? dayjs(activeStructure.end_date).format("DD-MMM-YYYY")
                                        : "present"}
                                    )
                                </small>

                                <Select
                                    mode="multiple"
                                    allowClear
                                    className="w-100 custom-select"
                                    placeholder="Select earnings"
                                    value={formData.selected_earning_ids}
                                    onChange={(value) => handleChange("selected_earning_ids", value)}
                                >
                                    {(activeStructure.earnings || []).map((earning: any) => (
                                        <Select.Option key={earning.id} value={earning.id}>
                                            {earning.earning_name} - ₹{earning.amount}
                                        </Select.Option>
                                    ))}
                                </Select>
                                {errors.selected_earning_ids && (
                                    <div className="text-danger mt-1">{errors.selected_earning_ids}</div>
                                )}
                            </div>
                        ) : (
                            <div className="col-md-12 mb-3">
                                <label className="form-label">Select Earnings</label>
                                <Select
                                    mode="multiple"
                                    disabled
                                    className="w-100 custom-select"
                                    placeholder="No active salary structure for this period"
                                    value={[]}
                                />
                                <small className="text-muted">
                                    Change Year/Month or pick a different employee.
                                </small>
                            </div>
                        )}

                        {/* Percents */}
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Employee Share (%)</label>
                            <InputNumber
                                min={0}
                                precision={2}
                                value={formData.employee_share_percent}
                                onChange={(value) => handleChange("employee_share_percent", value)}
                                style={{ width: "100%" }}
                            />
                            {errors.employee_share_percent && (
                                <div className="text-danger mt-1">{errors.employee_share_percent}</div>
                            )}
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Organization Share (%)</label>
                            <InputNumber
                                min={0}
                                precision={2}
                                value={formData.organization_share_percent}
                                onChange={(value) => handleChange("organization_share_percent", value)}
                                style={{ width: "100%" }}
                            />
                            {errors.organization_share_percent && (
                                <div className="text-danger mt-1">
                                    {errors.organization_share_percent}
                                </div>
                            )}
                        </div>

                        {/* Amounts (always two decimals) */}
                        <div className="col-md-6 mb-3">
                            <label className="form-label">Employee Share Amount</label>
                            <InputNumber
                                disabled
                                value={formData.employee_share_amount}
                                formatter={(v) => fmt2(v as any)}
                                parser={(v) => parse2(v)}
                                style={{ width: "100%" }}
                            />
                        </div>

                        <div className="col-md-6 mb-3">
                            <label className="form-label">Organization Share Amount</label>
                            <InputNumber
                                disabled
                                value={formData.organization_share_amount}
                                formatter={(v) => fmt2(v as any)}
                                parser={(v) => parse2(v)}
                                style={{ width: "100%" }}
                            />
                        </div>

                        {/* Status */}
                        <div className="col-md-12 mb-3">
                            <label className="form-label">Status</label>
                            <Select
                                value={formData.status}
                                onChange={(value) => handleChange("status", value)}
                                className={`w-100 ${errors.status ? "is-invalid-select" : ""}`}
                            >
                                {status.map((option) => (
                                    <Select.Option key={option.value} value={option.value}>
                                        {option.label}
                                    </Select.Option>
                                ))}
                            </Select>
                            {errors.status && <div className="text-danger mt-1">{errors.status}</div>}
                        </div>

                        {/* Description */}
                        <div className="col-md-12 mb-3">
                            <label className="form-label">Description</label>
                            <input
                                type="text"
                                className="form-control"
                                value={formData.description}
                                onChange={(e) => handleChange("description", e.target.value)}
                            />
                            {errors.description && (
                                <div className="text-danger mt-1">{errors.description}</div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="modal-footer">
                    <button
                        type="button"
                        className="btn btn-white border me-2"
                        onClick={() => {
                            handleCancel();
                            resetForm();
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleSubmit}
                        disabled={isLoading}
                    >
                        {isLoading ? (
                            <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                                aria-hidden="true"
                            />
                        ) : null}
                        {modalType === "edit" ? "Update Provident Fund" : "Add Provident Fund"}
                    </button>
                </div>
            </form>
        </Drawer>
    );
};

export default ProvidentFundModal;
